import { ApplicationRef, Component, ComponentFactory, ComponentFactoryResolver, ElementRef, Injector, Input, OnDestroy, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConflictComponent } from 'app/calendar/conflict/conflict';
import { NewEventComponent } from 'app/calendar/newEvent/newEvent';
import { StyleComponent } from 'app/components/style/style.component';
import { TooltipComponent } from 'app/components/tooltip/tooltip';
import { Config } from 'app/config.const';
import { Agenda } from 'app/model/agenda';
import { IIntervallo } from 'app/model/ambulatorio';
import { Appuntamenti, IDeleteGroupRequest, IDeleteRequest } from 'app/model/appuntamenti';
import { IAppuntamento } from 'app/model/appuntamento';
import * as moment from 'moment';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import { OAuth2Service } from '../../angular-rest';
import { ClinicStyle } from './../clinic-style/clinic-style.component';

import 'fullcalendar';
import { EventObjectInput, View } from 'fullcalendar';
import { EventSourceInput, OptionsInput } from 'fullcalendar/src/types/input-types';

import { CalendarBase } from 'app/calendar/calendar_base';
import 'qtip2';
import { buffer, combineLatest, debounceTime, delay, expand, filter, map, share, switchMap, tap } from 'rxjs/operators';
import { delayAsync, flatten } from 'utils';
import { WebSocketReadyState } from '../../angular-rest';
import { MeasureService } from '../../measure.service';
import { Giorno } from '../../model/time';

$.fn.qtip['zindex'] = 1000;

@Component({
	selector: 'full-calendar',
	templateUrl: 'fullcalendar.html',
	styleUrls: ['fullcalendar.component.css'],
	exportAs: 'calendar'
})
export class FullCalendarComponent extends CalendarBase implements OnDestroy {
	private static creationRequestId = -1;
	private static ambulatorioRequestId = -1;

	private _container: ElementRef;
	private wasClicked: boolean = false;

	@ViewChild('calendar')
	set container(value: ElementRef) {
		this._container = value;
	}

	get container(): ElementRef {
		return this._container;
	}

	private _agenda: Agenda;
	@Input()
	set agenda(value: Agenda) {
		this._agenda = value;
		this.init();
	}

	get agenda(): Agenda {
		return this._agenda;
	}

	private readonly toolTipFactory: ComponentFactory<TooltipComponent>;
	private slotDuration: moment.Duration;

	private newEvent = new Subject<moment.Moment>();
	private updateQueue = new Subject<IAppuntamento>();
	private deleteQueue = new Subject<IDeleteRequest<number> | IDeleteGroupRequest<number>>();

	private internalSubscription = new Subscription();
	private newEventSubscription: Subscription;

	constructor(
		private injector: Injector,
		private modalService: NgbModal,
		private app: ApplicationRef,
		private resolver: ComponentFactoryResolver,
		private readonly clinicStyle: ClinicStyle,
		private readonly measureService: MeasureService,
		auther: OAuth2Service
	) {
		super(auther);

		// Questa mi serve per iniettare in modo dinamico il tooltip.
		this.toolTipFactory = this.resolver.resolveComponentFactory(TooltipComponent);

		// Questa subscription invece mi trasforma i doppi click in delle eventRequests.
		this.newEventSubscription = this.newEvent.pipe(
			buffer(this.newEvent.pipe(debounceTime(Config.doubleClickTime))),
			filter(m => m.length === 2)
		).subscribe(m => {
			const date = m[0];

			this.upsertEvent({
				version: 1,
				categoria: "Paziente",
				evento: "Visita ambulatoriale",
				title: '',
				start: date.clone(),
				// @ts-ignore
				end: date.hasTime() ? date.clone().add(this.slotDuration) : null,
				status: 'U'
			});
		});

		// Questo è un fix per evitare di modificare come funziona il tutto in questo
		// momento. In particolare, la parte sottostante, che potrebbe essere stata
		// utilizzata come base in altri progetti.
		const onEventClick = (element: HTMLElement, calEvent: EventObjectInput) => {
			$(element).qtip('hide');
			this.upsertEvent(calEvent as IAppuntamento);
		};

		const makeDoubleClick = (element: HTMLElement, calEvent: EventObjectInput) => {
			let timeout;
			if (this.wasClicked) {
				this.wasClicked = false;
				clearTimeout(timeout);
				$(element).qtip('hide');
				this.upsertEvent(calEvent as IAppuntamento);
			} else {
				this.wasClicked = true;
				timeout = setTimeout(() => this.wasClicked = false, 500);
			}
		};

		this.onEventClickImpl = measureService.isMobile()
			? makeDoubleClick
			: onEventClick;
	}

	protected get settings(): Observable<OptionsInput> {
		// AGENDA_WEB: set impostazioni calendario
		return this.agenda.impostazioni.pipe(map(settings => {
			return <OptionsInput>{
				weekends: settings.mostra_weekend,
				minTime: moment.duration(settings.inizio_giornata),
				maxTime: moment.duration(settings.fine_giornata),
				defaultView: settings.vista,
				slotDuration: (this.slotDuration = moment.duration(settings.intervallo))
			};
		}))
	}

	private onEventClickImpl: (element: HTMLElement, calEvent: EventObjectInput) => any;

	// FullCalendar events.
	protected onEventClick(element: HTMLElement, calEvent: EventObjectInput) {
		this.onEventClickImpl(element, calEvent);
	}

	protected onDayClick(_, date: moment.Moment) {
		this.newEvent.next(date);
	}

	protected onEventDestroy(event: EventObjectInput, element: JQuery, view: View) {
		element.trigger('destroy');
	}

	protected onEventRender(event: EventObjectInput, element: JQuery, view: View): void {
		if (event.rendering != 'background') {
			// L'interno del qtip è un componente Angular!
			// In questo modo possiamo scriverlo come componente normale,
			// ma serve un po' di colla per inserirlo in un componente
			// jQuery.

			// Creo manualmente il componente!
			const tip = this.toolTipFactory.create(this.injector);

			// Passo il riferimento all'evento
			tip.instance.event = event as EventObjectInput & IAppuntamento;

			// Questo mi abilita gli eventi "OnChange"...
			this.app.attachView(tip.hostView);

			// Genero un evento di distruzione che richiamerò
			// quando il componente jQuery verrà distrutto.
			element.on('destroy', () => {
				tip.destroy();
			});

			// Carico qtip
			const qtip = element.qtip(<QTip2.QTipOptions>$.extend(true,
				{},
				this.measureService.isMobile()
					? Config.defaultMobileQtip2Options
					: Config.defaultQtip2Options,
				{
					content: {
						// Passandogli il componente angular
						// come contenuto!
						text: $(tip.location.nativeElement),
						title: event.title
					},
					position: {
						viewport: this.calendar
					}
				}
			));

			// Aggiungo la classe a seconda dell'evento
			element.addClass(StyleComponent.sanitize(tip.instance.event.evento));

			if (event.indirizzo != null) {
				element.append(
					`
	<span class="subtitle">
		${event.indirizzo} ${event.numciv}, ${event.citta})
	</span>
	`
				);
			}
		}
	}

	protected onEventResize(_, event: EventObjectInput): void {
		this.updateQueue.next($.extend(event as IAppuntamento, { local: true }));
	}

	protected onEventDrop(_, event: EventObjectInput): void {
		this.updateQueue.next($.extend(event as IAppuntamento, { local: true }));
	}

	protected onViewDestroy(view: View, element: JQuery): void { } // Non necessaria

	private onSocketConnected(appuntamenti: Appuntamenti) {
		this.internalSubscription.unsubscribe();
		this.internalSubscription = new Subscription();

		this.internalSubscription.add(appuntamenti.conflicts.subscribe(
			c => {
				const modalRef = this.modalService.open(
					ConflictComponent,
					{
						size: 'lg',
						backdrop: 'static',
						keyboard: false
					}
				);

				modalRef.componentInstance.conflict = c;
				modalRef.result.then(
					(event: IAppuntamento) => this.upsertEvent(event),
					() => { }
				);
			}
		));

		// Ora dobbiamo beccare gli aggiornamenti
		this.internalSubscription.add(this.updateQueue.asObservable()
			.subscribe(async evento => {
				if (evento.repeat) {
					const group_id = await this.agenda.createGroup();
					const version = +evento.version + 1;
					let start = evento.start.clone();
					let end = evento.end ? evento.end.clone() : null;
					const increment = moment.duration(
						evento.repeat.increment,
						<any>evento.repeat.incrementUnit
					);
					if (evento.repeat.type == 'occorrenze') {
						for (var i = 0; i < evento.repeat.incrementNumber; i++) {
							appuntamenti.upsert({
								group_id,
								id: evento.id,
								title: evento.title,
								start: start.clone(),
								end: end ? end.clone() : null,
								indirizzo: evento.indirizzo,
								numciv: evento.numciv,
								citta: evento.citta,
								CAP: evento.CAP,
								provincia: evento.provincia,
								descrizione: evento.descrizione,
								categoria: evento.categoria,
								evento: evento.evento,
								version: version,
								status: evento.status
							});
							start = start.add(increment);
							end = end ? end.add(increment) : end;
						}
					} else {
						const until = evento.repeat.incrementUntil.clone().endOf('day');

						while (start.isSameOrBefore(until)) {
							appuntamenti.upsert({
								group_id,
								id: evento.id,
								title: evento.title,
								start: start.clone(),
								end: end ? end.clone() : null,
								indirizzo: evento.indirizzo,
								numciv: evento.numciv,
								citta: evento.citta,
								CAP: evento.CAP,
								provincia: evento.provincia,
								descrizione: evento.descrizione,
								categoria: evento.categoria,
								evento: evento.evento,
								version: version,
								status: evento.status
							});
							start = start.add(increment);
							end = end ? end.add(increment) : end;
						}
					}
				} else {
					appuntamenti.upsert({
						id: evento.id,
						title: evento.title,
						start: evento.start,
						end: evento.end,
						indirizzo: evento.indirizzo,
						numciv: evento.numciv,
						citta: evento.citta,
						CAP: evento.CAP,
						provincia: evento.provincia,
						descrizione: evento.descrizione,
						categoria: evento.categoria,
						evento: evento.evento,
						version: +evento.version + 1,
						status: evento.status
					});
				}
			})
		);

		this.internalSubscription.add(this.deleteQueue.asObservable()
			.subscribe(delete_request => {
				if ('delete' in delete_request) {
					appuntamenti.delete(delete_request['delete']);
				} else {
					appuntamenti.delete_group(delete_request['delete_group']);
				}
			})
		);
	}

	protected onViewRender(view: View, element: JQuery): [Observable<IAppuntamento>, Observable<IDeleteRequest<number>>] {
		// Mi connetto al WebSocket
		const socket: Observable<Appuntamenti> = new Observable<Appuntamenti>(observer => {
			setTimeout(() => {
				// @ts-ignore
				observer.next(this.agenda.getEventSocket(view.start, view.end));
				observer.complete();
			});
		}).pipe(
			// In caso di chiusura del socket, riprovo dopo 1 sec
			expand(current => current.status.pipe(
				delay(1000),
				// @ts-ignore
				filter(state => state === WebSocketReadyState.CLOSED),
				// @ts-ignore
				map(() => {
					//console.log('CLOSED MAP! ', current)
					return this.agenda.getEventSocket(view.start, view.end)
				})
			)),
			tap(current => {
				/* console.log('CLOSED!', current)
				console.log(stackTrace())
				console.log('**********************') */
				this.onSocketConnected(current)
			}),
			share()
		);

		// Questo è l'unico punto in cui posso agganciarmi al socket per gli 
		// aggiornamenti!

		// Qui devo solo restituire ciò che mi passa il socket
		return [
			socket.pipe(switchMap(current => current.updates)),
			socket.pipe(switchMap(current => current.deletes))
		];
	}

	private ambulatiorioSubscription = new Subscription();

	protected getBusinessHours(view: View): Observable<IIntervallo[][]> | false {
		this.ambulatiorioSubscription.unsubscribe();
		this.ambulatiorioSubscription = new Subscription();

		// Nella visuale "mese" non serve l'orario completo, ma solo i giorni,
		// che vengono gestiti dal componente separato CalendarStyle, per 
		// superare delle limitazioni di FullCalendar.
		if (view.type != 'month') {
			// AGENDA_WEB: questo viene chiamato quando si seleziona una visualizzazione del calendario che non è quella mensile
			const weekStart = view.start.startOf('isoWeek');
			const ret = this.agenda.alternativa.pipe(
				switchMap(week =>
					Observable.combineLatest(...(
						week.find(s =>
							// @ts-ignore
							weekStart.isSame(moment.utc(s.settimana))
							// AGENDA_WEB: concateno gli orari d'ambulatorio a quelli ScT, in modo da porterli usare dopo
						) || (() => {
							const a = this.agenda.ambulatorio.concat(this.agenda.ambulatorioScT as Giorno[]) as Giorno[]
							return a

						})()
					)
						.map(amb => amb.pipe(
							switchMap(pp => pp.length ?
								Observable.combineLatest(...pp) :
								Observable.of([] as IIntervallo[])
							)
						))
					)
				),
				tap(week => this.clinicStyle.days = week),
			);

			this.ambulatiorioSubscription.add(
				ret.pipe(
					combineLatest(this.clinicStyle.mappings)
				).subscribe(async ([days, mappings]) => {
					if (this.calendar) {
						// Questo forza l'esecuzione del codice
						// seguente al prossimo tick, riordinando
						// quindi gli eventi in modo corretto.
						await delayAsync();

						this.calendar.fullCalendar(
							'removeEventSource',
							'ambulatorio'
						);
						this.calendar.fullCalendar(
							'removeEventSource',
							'ambulatorioScT'
						)

						this.calendar.fullCalendar(
							'addEventSource',
							{
								id: 'ambulatorio',
								events: flatten(days.slice(0, 7)
								.map((intervals, day) =>
									intervals
										.map(interval => {
											// AGENDA_WEB: qui vengono renderizzati gli orari
											let name = interval.ambulatorio ? "-name" : "";
											let index = interval.ambulatorio ?
												mappings.get(interval.ambulatorio.toLowerCase()) :
												FullCalendarComponent.ambulatorioRequestId--;
											const event = {
												// @ts-ignore
												title: interval.ambulatorio ? interval.ambulatorio : " ",
												rendering: `background`,
												// @ts-ignore
												start: moment.utc(weekStart).add(day, 'days')
													.add(interval.inizio),
												// @ts-ignore
												end: moment.utc(weekStart).add(day, 'days')
													.add(interval.fine),
												className: [
													`clinic-style${name}-${index}`,
													`clinic-style${name}`
												]
											}
											// @ts-ignore
											return (<EventObjectInput>event)
										})
								))
							} as EventSourceInput
						);

						/**
						 * AGENDA_WEB: si deve aggiungere una nuova sorgente degli eventi chiamata ambulatorioScT
						 * vengono presi in considerazione solo gli ultimi 7 elementi dell'array degli orari (gli altri sono quelli degli orari non ScT)
						 */
						this.calendar.fullCalendar(
							'addEventSource',
							{
								id: 'ambulatorioScT',
								events: flatten(days.slice(7)
								.map((intervals, day) =>
									intervals
										.map(interval => {
											// AGENDA_WEB: qui vengono renderizzati gli orari
											let name = interval.ambulatorio ? "-name" : "";
											let index = interval.ambulatorio ?
												mappings.get(interval.ambulatorio.toLowerCase()) :
												FullCalendarComponent.ambulatorioRequestId--;
											const event = {
												// @ts-ignore
												title: (interval.ambulatorio && interval.ambulatorio.length > 0) ? interval.ambulatorio : " ",
												rendering: `background`,
												// @ts-ignore
												start: moment.utc(weekStart).add(day, 'days')
													.add(interval.inizio),
												// @ts-ignore
												end: moment.utc(weekStart).add(day, 'days')
													.add(interval.fine),
												backgroundColor: 'gray'
											}

											// @ts-ignore
											return (<EventObjectInput>event)
										})
								))
							} as EventSourceInput
						);
					}
				})
			)

			return ret;
		} else {
			return false;
		}
	}

	protected onCalendarDestroy() {
		this.internalSubscription.unsubscribe();
		this.internalSubscription = new Subscription();
	}


	protected init() {
		// Piccolo override: inizializziamo solo se c'è
		// un'agenda corrispondente.
		if (this.agenda) {
			super.init();
		}
	}

	upsertEvent(event?: IAppuntamento) {
		if (!event) {
			event = {
				version: 1,
				categoria: null,
				evento: null,
				title: '',
				start: moment.utc(),
				end: moment.utc(),
				status: 'U'
			};
		}

		if (!event.id) {
			// FIXME: gli id dovrebbero essere numerici dovunque...
			event.id = `${FullCalendarComponent.creationRequestId--}`;
		}

		const modalRef = this.modalService.open(NewEventComponent);
		const component = modalRef.componentInstance as NewEventComponent;
		component.settings$ = this.agenda.impostazioni;
		component.setEvento(event);
		modalRef.result.then((event: IAppuntamento | { delete: number } | { delete_group: number }) => {
			if ('start' in event) { // TODO: typings
				this.updateQueue.next(event);
			} else {
				this.deleteQueue.next(<any>event);
			}
		}, () => { });
	}

	ngOnDestroy() {
		super.ngOnDestroy();
		this.newEventSubscription.unsubscribe();
	}
}



// WEBPACK FOOTER //
// ./src/app/calendar/full-calendar/fullcalendar.component.ts